import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, UserCheck, UserX, Activity, Shield, Eye, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SecurityModule = () => {
  const [users, setUsers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showUserActivity, setShowUserActivity] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivity, setUserActivity] = useState([]);
  const [availableModules, setAvailableModules] = useState({});
  const [editingPermissions, setEditingPermissions] = useState({});
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'Staff'
  });

  useEffect(() => {
    loadUsers();
    loadActivityLogs();
    loadAvailableModules();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadActivityLogs = async () => {
    try {
      const response = await axios.get(`${API}/activity-logs?limit=50`);
      setActivityLogs(response.data);
    } catch (error) {
      console.error('Failed to load activity logs', error);
    }
  };

  const loadAvailableModules = async () => {
    try {
      const response = await axios.get(`${API}/permissions/modules`);
      setAvailableModules(response.data);
    } catch (error) {
      console.error('Failed to load modules', error);
    }
  };

  const loadUserActivity = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/activity?limit=30`);
      setUserActivity(response.data);
    } catch (error) {
      toast.error('Failed to load user activity');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(`${API}/users`, newUser);
      toast.success('User created successfully!');
      setShowAddUser(false);
      setNewUser({ name: '', email: '', password: '', role: 'Staff' });
      loadUsers();
      loadActivityLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/users/${userId}`, { is_active: !currentStatus });
      toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      loadUsers();
      loadActivityLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      // Get default permissions for new role
      const defaultPermsResponse = await axios.get(`${API}/permissions/defaults/${newRole}`);
      const defaultPerms = defaultPermsResponse.data;
      
      await axios.put(`${API}/users/${userId}`, { 
        role: newRole,
        permissions: defaultPerms  // Reset to default permissions when role changes
      });
      toast.success('User role updated successfully');
      loadUsers();
      loadActivityLogs();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const handleOpenPermissions = (user) => {
    setSelectedUser(user);
    setEditingPermissions(user.permissions || {});
    setShowPermissions(true);
  };

  const handleTogglePermission = (module) => {
    setEditingPermissions(prev => ({
      ...prev,
      [module]: !prev[module]
    }));
  };

  const handleSavePermissions = async () => {
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, { 
        permissions: editingPermissions 
      });
      toast.success('Permissions updated successfully');
      setShowPermissions(false);
      loadUsers();
      loadActivityLogs();
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login': return '🔐';
      case 'create_user': return '👤➕';
      case 'update_user': return '✏️';
      case 'deactivate_user': return '🚫';
      case 'create_quote': return '📝';
      case 'create_invoice': return '🧾';
      default: return '📋';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Admin':
      case 'Owner':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'Manager':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'Staff':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'Customer':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-primary text-xl font-mono uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
              SECURITY MODULE
            </h1>
            <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
              User Management & Activity Monitoring
            </p>
          </div>
          <Button
            onClick={() => setShowAddUser(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider"
          >
            <Plus size={16} className="mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="border-border">
          <CardContent className="p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
              Total Users
            </p>
            <p className="text-3xl font-bold font-mono text-primary">
              {users.length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
              Active Users
            </p>
            <p className="text-3xl font-bold font-mono text-green-500">
              {users.filter(u => u.is_active).length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
              Admins
            </p>
            <p className="text-3xl font-bold font-mono text-red-500">
              {users.filter(u => u.role === 'Admin' || u.role === 'Owner').length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
              Recent Activity
            </p>
            <p className="text-3xl font-bold font-mono text-blue-500">
              {activityLogs.length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="border-border mb-6">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-2">
            <Shield size={20} />
            User Accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Name</th>
                  <th className="text-left py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Email</th>
                  <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Role</th>
                  <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Status</th>
                  <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Last Login</th>
                  <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-border hover:bg-accent/50 transition-colors">
                    <td className="py-3 px-2 font-medium">{user.name}</td>
                    <td className="py-3 px-2 font-mono text-sm">{user.email}</td>
                    <td className="py-3 px-2 text-center">
                      <select
                        value={user.role}
                        onChange={(e) => handleUpdateUserRole(user.id, e.target.value)}
                        className={`px-3 py-1 rounded border font-mono text-xs uppercase ${getRoleBadgeColor(user.role)}`}
                      >
                        <option value="Admin">Admin</option>
                        <option value="Manager">Manager</option>
                        <option value="Staff">Staff</option>
                        <option value="Customer">Customer</option>
                      </select>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`px-3 py-1 rounded-sm border font-mono text-xs uppercase ${
                        user.is_active 
                          ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                          : 'bg-red-500/20 text-red-400 border-red-500/30'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center font-mono text-xs text-muted-foreground">
                      {formatDate(user.last_login)}
                    </td>
                    <td className="py-3 px-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleOpenPermissions(user)}
                          className="text-purple-500 hover:text-purple-400"
                          title="Manage Permissions"
                        >
                          <Shield size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowUserActivity(true);
                            loadUserActivity(user.id);
                          }}
                          className="text-blue-500 hover:text-blue-400"
                          title="View Activity"
                        >
                          <Eye size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                          className={user.is_active ? 'text-red-500 hover:text-red-400' : 'text-green-500 hover:text-green-400'}
                          title={user.is_active ? 'Deactivate User' : 'Activate User'}
                        >
                          {user.is_active ? <UserX size={16} /> : <UserCheck size={16} />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity Log */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground flex items-center gap-2">
            <Activity size={20} />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {activityLogs.slice(0, 20).map((log) => (
              <div key={log.id} className="p-3 bg-card border border-border rounded-sm hover:border-primary/30 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <span className="text-2xl">{getActionIcon(log.action)}</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        <span className="text-primary">{log.user_name}</span>
                        <span className="text-muted-foreground"> • {log.action.replace('_', ' ')}</span>
                      </p>
                      {log.details && (
                        <p className="text-xs text-muted-foreground mt-1">{log.details}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1 font-mono">{log.user_email}</p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                    {formatDate(log.timestamp)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-sm max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-black tracking-tight uppercase text-foreground">Add New User</h2>
                <button 
                  onClick={() => setShowAddUser(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="uppercase text-xs tracking-wide">Full Name</Label>
                  <Input
                    id="name"
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    placeholder="John Doe"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="uppercase text-xs tracking-wide">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    placeholder="john@example.com"
                    className="mt-1 font-mono"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="uppercase text-xs tracking-wide">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    placeholder="••••••••"
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Minimum 6 characters recommended</p>
                </div>

                <div>
                  <Label htmlFor="role" className="uppercase text-xs tracking-wide">Role</Label>
                  <select
                    id="role"
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm font-mono text-sm"
                  >
                    <option value="Staff">Staff</option>
                    <option value="Manager">Manager</option>
                    <option value="Admin">Admin</option>
                    <option value="Customer">Customer</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {newUser.role === 'Customer' && 'Limited access for client portal'}
                    {newUser.role === 'Staff' && 'Basic access for team members'}
                    {newUser.role === 'Manager' && 'Extended access for supervisors'}
                    {newUser.role === 'Admin' && 'Full system access'}
                  </p>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase"
                  >
                    Create User
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddUser(false)}
                    className="font-mono uppercase"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* User Activity Modal */}
      {showUserActivity && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowUserActivity(false)}>
          <div className="bg-card border border-border rounded-sm max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-black tracking-tight uppercase text-foreground">{selectedUser.name}</h2>
                  <p className="text-sm font-mono text-muted-foreground">{selectedUser.email}</p>
                </div>
                <button 
                  onClick={() => setShowUserActivity(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-2">
                <h3 className="text-lg font-semibold uppercase mb-4">Activity History</h3>
                {userActivity.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No activity recorded yet</p>
                ) : (
                  userActivity.map((log) => (
                    <div key={log.id} className="p-3 bg-card border border-border rounded-sm">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <span className="text-xl">{getActionIcon(log.action)}</span>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-primary">{log.action.replace('_', ' ')}</p>
                            {log.details && (
                              <p className="text-xs text-muted-foreground mt-1">{log.details}</p>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground font-mono">
                          {formatDate(log.timestamp)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Management Modal */}
      {showPermissions && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowPermissions(false)}>
          <div className="bg-card border border-border rounded-sm max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-black tracking-tight uppercase text-foreground flex items-center gap-2">
                    <Shield size={24} className="text-purple-500" />
                    Module Permissions
                  </h2>
                  <p className="text-sm font-mono text-muted-foreground mt-1">
                    {selectedUser.name} • {selectedUser.role}
                  </p>
                </div>
                <button 
                  onClick={() => setShowPermissions(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-sm">
                <p className="text-sm text-blue-400">
                  💡 <strong>Tip:</strong> Permissions control which modules users can access. 
                  Changes take effect immediately upon next login.
                </p>
              </div>

              <div className="space-y-3">
                {Object.entries(availableModules).map(([moduleKey, moduleInfo]) => {
                  const hasPermission = editingPermissions[moduleKey] || false;
                  
                  return (
                    <div 
                      key={moduleKey}
                      className={`p-4 border rounded-sm transition-all cursor-pointer ${
                        hasPermission 
                          ? 'bg-green-500/10 border-green-500/30 hover:bg-green-500/20' 
                          : 'bg-card border-border hover:bg-accent/50'
                      }`}
                      onClick={() => handleTogglePermission(moduleKey)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                              hasPermission ? 'bg-green-500/20' : 'bg-muted'
                            }`}>
                              {hasPermission ? (
                                <UserCheck size={24} className="text-green-500" />
                              ) : (
                                <UserX size={24} className="text-muted-foreground" />
                              )}
                            </div>
                            <div>
                              <p className="font-semibold text-foreground uppercase tracking-wide">
                                {moduleInfo.name}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {moduleInfo.description}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className={`px-4 py-2 rounded border font-mono text-xs uppercase font-bold ${
                          hasPermission 
                            ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                            : 'bg-red-500/20 text-red-400 border-red-500/30'
                        }`}>
                          {hasPermission ? 'Granted' : 'Denied'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-3 pt-6 border-t border-border mt-6">
                <Button
                  onClick={handleSavePermissions}
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase"
                >
                  Save Permissions
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowPermissions(false)}
                  className="font-mono uppercase"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityModule;
